import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeMount, onUnmounted, ref } from 'vue';
import { debounce } from 'lodash-es';

import {
  SearchBar,
  LoaderIndicator,
  HelperViewStatus,
  DataTable,
  ButtonIconPrimary
} from '@warrenbrasil/nebraska-web';
import { FilterSelectButton } from '@/modules/common/components/FilterSelectButton';
import { SidebarFilters } from '@/modules/catalog/components/SidebarFilters';
import { EmptyHelper } from '@/modules/catalog/components/EmptyHelper';

import { registerObserver } from '@/modules/catalog/views/Products/helpers/registerObserver';
import { columns } from './columns';
import {
  getFixedIncomeProducts,
  IFixedIncomeProduct,
  IFixedIncomeFilter,
  IPagination,
  IProductsParams
} from '@/modules/catalog/services/products';

interface Props {
  isBuilder: boolean;
}

export default /*#__PURE__*/_defineComponent({
  __name: 'FixedIncomeProducts',
  props: {
    isBuilder: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
  isBuilder: boolean;
}



const products = ref<IFixedIncomeProduct[]>([]);
const filters = ref<IFixedIncomeFilter[]>([]);
const pagination = ref<IPagination>();

const params = ref<IProductsParams>({
  pageIndex: 1,
  pageSize: 10
});

const isLoading = ref(true);
const isScrolling = ref(false);
const isSorting = ref(false);
const hasError = ref(false);
const isSearchBarHover = ref(false);
const isSearchBarFocus = ref(false);

const searchValue = ref('');
const isFiltersOpen = ref(false);
const clearFilters = ref(false);
const filtersCount = ref(0);
const selectedFilters = ref<Record<string, string | string[]>>({});
const scrollTop = ref(document.documentElement.scrollTop);

const columnsTable = computed(() => {
  return columns(pagination.value!.totalRecords);
});

const hasScrolled = computed(() => {
  return scrollTop.value >= 100;
});

const isEmpty = computed(() => {
  return products.value.length === 0;
});

const emptyDataType = computed(() => {
  return searchValue.value ? 'search' : 'filter';
});

const scrolledButtonClass = computed(() => {
  return props.isBuilder
    ? 'fixed-income-products__button builder'
    : 'fixed-income-products__button';
});

const onInputChange = debounce(handleSearch, 500);

function openFilters() {
  isFiltersOpen.value = true;
}

function closeFilters() {
  isFiltersOpen.value = false;
}

async function handleClear() {
  if (searchValue.value) {
    searchValue.value = '';

    handleSearch();
  } else clearFilters.value = true;
}

async function applyFilters(
  applyedFilters: IFixedIncomeFilter[],
  count: number
) {
  clearFilters.value = false;
  filtersCount.value = count;

  if (applyedFilters.length === 0) {
    selectedFilters.value = {};
  } else {
    selectedFilters.value = {};

    applyedFilters.forEach(filter => {
      selectedFilters.value = {
        ...selectedFilters.value,
        [filter.value]: filter.selectedFilters
      };
    });
  }

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField: undefined,
    sortDirection: undefined
  };

  closeFilters();

  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

async function onSort(index: number, isSort: boolean, value: string) {
  const sortDirection =
    index === -1 ? undefined : isSort ? 'Ascending' : 'Descending';
  const sortField = index === -1 ? undefined : value;

  params.value = {
    ...params.value,
    pageIndex: 1,
    sortField,
    sortDirection
  };

  await fetchFixedIncomeProducts(false, false, true);
  registerObserver(fetchFixedIncomeProducts);
}

async function handleSearch() {
  params.value = {
    pageSize: 10,
    pageIndex: 1,
    searchTerm: searchValue.value
  };

  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

async function retry() {
  await fetchFixedIncomeProducts();
  registerObserver(fetchFixedIncomeProducts);
}

function goToTop() {
  document.documentElement.scrollTo(0, 0);
}

async function fetchFixedIncomeProducts(
  loading = true,
  scrolling = false,
  sorting = false
) {
  try {
    isLoading.value = loading;
    isScrolling.value = scrolling;
    isSorting.value = sorting;
    hasError.value = false;

    if (isScrolling.value) {
      if (!pagination.value?.nextPage) return;

      params.value = {
        ...params.value,
        pageIndex: pagination.value.nextPage
      };
    }

    const data = await getFixedIncomeProducts(
      selectedFilters.value,
      params.value
    );

    filters.value = data.filters;
    pagination.value = data.pagination;

    if (isScrolling.value) {
      products.value.push(...data.items);
      return;
    }

    products.value = data.items;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
    isScrolling.value = false;
    isSorting.value = false;
  }
}

function handleScrollTop() {
  scrollTop.value = document.documentElement.scrollTop;
}

onBeforeMount(async () => {
  await fetchFixedIncomeProducts();

  window.addEventListener('scroll', handleScrollTop);

  registerObserver(fetchFixedIncomeProducts);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScrollTop);
});

return { __sfc: true,props, products, filters, pagination, params, isLoading, isScrolling, isSorting, hasError, isSearchBarHover, isSearchBarFocus, searchValue, isFiltersOpen, clearFilters, filtersCount, selectedFilters, scrollTop, columnsTable, hasScrolled, isEmpty, emptyDataType, scrolledButtonClass, onInputChange, openFilters, closeFilters, handleClear, applyFilters, onSort, handleSearch, retry, goToTop, fetchFixedIncomeProducts, handleScrollTop, SearchBar, LoaderIndicator, HelperViewStatus, DataTable, ButtonIconPrimary, FilterSelectButton, SidebarFilters, EmptyHelper }
}

})